﻿import { Component, Inject, Input } from "@fiqas/angular-utils";
import * as Rx from "rxjs/Rx";
import { FIQAssist } from "../../app";
import { IDisposable } from "../../interfaces/general";
import { CollectionResult } from "../../interfaces/iFqs";
import { CompactServiceCase } from "../../models/serviceCase";
import { User } from "../../models/user";
import { AuthorizationService } from "../../services/authorizationService";
import { CacheService } from "../../services/cache";
import { DialogService } from "../../services/dialogService";
import { ExportService } from "../../services/exportToExcelService";
import { ServiceCaseService } from "../../services/fiqassistApi/serviceCaseService";
import { IsProcessingService } from "../../services/isProcessingService";
import { LogService } from "../../services/logService";
import { ServiceUtils } from "../../services/serviceBase";
import { ServiceCaseSearchService } from "../../services/serviceCaseSearchService";
import { TranslationService } from "../../services/TranslationService";
import { Application } from "../application";
import { ControlBase } from "../general/controlBase";
import { SaveDialog } from "../general/savedialog";
import { SearchTab } from "./searchtab";

@Component({
    module: FIQAssist,
    selector: "simpleSearch",
    templateUrl: "views/simpleSearch.html",
    require: {
        "application": "^application",
        "searchTab": "^searchTab"
    }
})
export class SimpleSearch extends ControlBase {
    constructor(
        @Inject('$timeout') $timeout: ng.ITimeoutService,
        @Inject('$rootRouter') rootRouter: angular.Router,
        @Inject() saveDialog: SaveDialog,
        @Inject() authorizationService: AuthorizationService,
        @Inject() private serviceCaseService: ServiceCaseService,
        @Inject() private isProcessingService: IsProcessingService,
        @Inject() private translationService: TranslationService,
        @Inject() private cacheService: CacheService,
        @Inject() private logService: LogService,
        @Inject() private dialogService: DialogService,
        @Inject() private exportService: ExportService,
        @Inject() private serviceCaseSearchService: ServiceCaseSearchService,
    ) {
        super(saveDialog, rootRouter, $timeout, authorizationService);
        this._searchPhrase = '';
    }

    public async $routerOnActivate(next?: angular.ComponentInstruction, prev?: angular.ComponentInstruction) {
         await this.authorizationService.roleLoaded;
    }

    public async $routerOnReuse(next?: angular.ComponentInstruction, prev?: angular.ComponentInstruction) {
         await this.authorizationService.roleLoaded;
    }

    public application: Application;
    private _searchPhrase: string;
    private hint: string;
    private disposables: IDisposable[] = [];

    @Input() public requiresactionlabel: string;
    private actionRequiredLoading: boolean = false;
    private actionRequiredLevel: string;
    private actionRequiredCount: number;
    private requiresactionhint: string = "";

    @Input() public needsfeedbacklabel: string;
    private awaitingFeedbackLoading: boolean = false;
    private awaitingFeedbackLevel: string;
    private awaitingFeedbackCount: number;
    private needsfeedbackhint: string = "";

    @Input() public activecaseslabelmulti: string;
    @Input() public activecaseslabelsingle: string;
    public get activecaseslabel(): string {
        return (this.activeCasesCount == 1) ? this.activecaseslabelsingle : this.activecaseslabelmulti;
    }
    private activeCasesLoading: boolean = false;
    private activeCasesCount: number;
    private activecaseshint: string = "";

    private initialized: boolean = false;
    private onSearchTextChanged: Rx.Subject<string>;
    private _collapsed: boolean;

    @Input()
    public get collapsed(): boolean {
        return this._collapsed;
    }
    public set collapsed(val: boolean) {
        this._collapsed = val;
    }

    @Input() public searchhint: string;

    public logedInUsername: string;
    public showtextnoresults: boolean;
    private tomuch: boolean = false;
    private searchTab: SearchTab;
    private lastSearchMode = 0;
    public SearchResult: CompactServiceCase[];
    public SearchResultCount: number;
    private searchResultPageCount: number;

    $onInit() {
        var fss = this.application.onFastSearch.subscribe(([phrase, result]) => {
            this._searchPhrase = phrase;
            this.processResults(phrase, result);
        });
        this.application.user.then(x => { x.personId.value.then(y => this.logedInUsername = y.name) })
        this.disposables.push(() => fss.unsubscribe());
        this._myservicecases = this.cacheService.getValue<boolean>("simplesearch.myservicecases") || false;
        this.searchResultPageCount = this.cacheService.getValue<number>("simplesearch.resultsPageCount") || null;
        this.tomuch = this.checkTooManyResults();
        this.showtextnoresults = false;
        this.initialized = true;
        this.onSearchTextChanged = new Rx.Subject();
        var onSearchSubscription = this.onSearchTextChanged.debounceTime(400).subscribe({ next: searchString => this.Search(searchString) });
        this.disposables.push(() => onSearchSubscription.unsubscribe());
        this.disposables.push(() => this.authorizationService.onRoleChanged(_ => this.init()).unsubscribe());
        this.disposables.push(this.application.addOnAccountsChanged(s => this.selectedAccountsChanged(s)));
        this.init();
    }

    private async init() {
        this.getCounters();
        this.hint = this.searchhint;
        var _mode = this.application.simpleSearchMode;
        var searchPhrase = this.application.simpleSearchString;
        switch (_mode) {
            case 1:
                this.onSearchValueChanged(searchPhrase || "");
                break;
            case 5:
                if (this.serviceCaseSearchService.CurrentResult) {
                    this.lastSearchMode = 5;
                    this._searchPhrase = searchPhrase;
                    this.processResults(searchPhrase, this.serviceCaseSearchService.CurrentResult);
                }
                break;
            case 2:
                this.onActionRequiredClicked();
                break;
            case 3:
                this.onAwaitingFeedbackClicked();
                break;
            case 4:
                this.onActiveCasesClicked();
                break;
        }

    }

    $onDestroy() {
        this.application.simpleSearchMode = this.SearchResult && this.SearchResultCount > 0 ? this.lastSearchMode : 0;
        this.application.simpleSearchString = this._searchPhrase;
        this.disposables.forEach(d => {
            try { d() } catch (e) { this.logService.warning('SimpleSearch', 'Error disposing', e); }
        });
        this.currentSearch = null;
        this.cacheService.setValue("simplesearch.myservicecases", this._myservicecases);
        this.cacheService.setValue<number>("simplesearch.resultsPageCount", (this.searchResultPageCount));
    }

    private async loadAwaitingFeedbackCounter(custIds: number[]) {
        try {
            const count = await this.serviceCaseService.AwaitingFeedbackCount(custIds, this._myservicecases);
            const level = count > 0 ? (count > 5 ? 'high' : 'medium') : 'low';
            const hint = await this.translationService.TranslateAsync(
                count == 0 ? "NO_CASES_REQUIRE_FEEDBACK" :
                    count == 1 ? "ONE_CASE_REQUIRES_FEEDBACK" :
                        "XXX_CASES_REQUIRE_FEEDBACK").then(t => t.replace("XXX", count.toString()));

            return { count, level, hint }
        } catch (e) {
            return { count: 0, level: 'high', hint: '' }
        }
    }

    private async loadActionRequiredCounter(custIds: number[]) {
        try {
            const count = await this.serviceCaseService.ActionRequiredCount(custIds, this._myservicecases);
            const level = count > 0 ? (count > 5 ? 'high' : 'medium') : 'low';
            const hint = await this.translationService.TranslateAsync(
                count == 0 ? "NO_CASES_REQUIRE_ACTION" :
                    count == 1 ? "ONE_CASE_REQUIRES_ACTION" :
                        "XXX_CASES_REQUIRE_ACTION").then(t => t.replace("XXX", count.toString()));

            return { count, level, hint }
        } catch (e) {
            return { count: 0, level: 'high', hint: '' }
        }
    }

    private async loadActiveCasesCounter(custIds: number[]) {
        try {
            const count = await this.serviceCaseService.ActiveCasesCount(custIds, this._myservicecases);
            const hint = await this.translationService.TranslateAsync(
                count == 0 ? "NO_ACTIVE_CASES" :
                    count == 1 ? "ONE_ACTIVE_CASE" :
                        "XXX_ACTIVE_CASES").then(t => t.replace("XXX", count.toString()));

            return { count, hint }
        } catch (e) {
            return { count: 0, hint: '' }
        }
    }

    private countersRequestId: number = 0;
    private getCounters() {
        const loadId = ++this.countersRequestId;
        try {
            var custIds = this.application.selectedCustomers.map(x => x.key);
            if (custIds.length > 0) {
                this.actionRequiredLoading = true;
                this.loadActionRequiredCounter(custIds).then(stats => {
                    if (loadId !== this.countersRequestId) return;

                    this.actionRequiredLoading = false;
                    this.actionRequiredCount = stats.count;
                    this.actionRequiredLevel = stats.level;
                    this.requiresactionhint = stats.hint;

                });
                this.awaitingFeedbackLoading = true;
                this.loadAwaitingFeedbackCounter(custIds).then(stats => {
                    if (loadId !== this.countersRequestId) return;

                    this.awaitingFeedbackLoading = false;
                    this.awaitingFeedbackCount = stats.count;
                    this.awaitingFeedbackLevel = stats.level;
                    this.needsfeedbackhint = stats.hint;

                });
                this.activeCasesLoading = true;
                this.loadActiveCasesCounter(custIds).then(stats => {
                    if (loadId !== this.countersRequestId) return;

                    this.activeCasesLoading = false;
                    this.activeCasesCount = stats.count;
                    this.activecaseshint = stats.hint;

                });
            } else {
                this.activeCasesCount = this.actionRequiredCount = this.awaitingFeedbackCount = 0;
                this.actionRequiredLevel = this.awaitingFeedbackLevel = 'low';
                this.translationService.TranslateAsync("NO_CASES_REQUIRE_FEEDBACK").then(t => this.needsfeedbackhint = t);
                this.translationService.TranslateAsync("NO_CASES_REQUIRE_ACTION").then(t => this.requiresactionhint = t);
                this.translationService.TranslateAsync("NO_ACTIVE_CASES").then(t => this.activecaseshint = t);
            }
        } finally {

        }
    }

    public onSearchValueChanged(value: string) {
        this._searchPhrase = value;
        if (this._searchPhrase.length > 0) {
            this.hint = '';
            this.onSearchTextChanged.next(this._searchPhrase);
        } else if (!this.hint) {
            this.hint = this.searchhint;
        }
        this.lastSearchMode = 1;
    }

    private _currentSearchSubscription: Rx.Subscription;
    private get currentSearch(): Rx.Subscription {
        return this._currentSearchSubscription;
    }
    private set currentSearch(subscription: Rx.Subscription) {
        if (this._currentSearchSubscription) {
            this._currentSearchSubscription.unsubscribe();
        }
        this._currentSearchSubscription = subscription;
    }

    private _myservicecases: boolean;
    private get myservicecases(): boolean {
        return this._myservicecases;
    }
    private changemyservicecases(val: boolean) {
        this._myservicecases = val;
        switch (this.lastSearchMode) {
            case 1:
                if (this._searchPhrase)
                    this.Search(this._searchPhrase);
                break;
            case 2:
                this.onActionRequiredClicked();
                break;
            case 3:
                this.onAwaitingFeedbackClicked();
                break;
            case 4:
                this.onActiveCasesClicked();
                break;
        }
        this.getCounters();
    }
    private user: User;
    public async Search(searchString: string) {
        if (!searchString) {
            return;
        }
        if (!this.user) {
            this.user = await this.application.user;
        }
        if (this.application.selectedCustomers && this.application.selectedCustomers.length > 0) {
            this.showtextnoresults = false;
            this.currentSearch = null;
            this.isProcessingService.StartProcessing();
            var sub = this.serviceCaseSearchService.Search(searchString, this.application.selectedCustomers.map(v => v.key), 100, 0, (this._myservicecases) ? this.user : undefined)
                .subscribe({
                    next: result => this.processResults(searchString, result),
                    error: error => {
                        if (ServiceUtils.IsUnauthorizedResponse(error)) {
                            return;
                        }
                        this.application.setSeqUrl(error);
                        this.logService.error('SimpleSearch', 'Error while searching', error);
                        this.dialogService.showErrorDialog('error', 'ERROR', 'SIMPLE_SEARCH_ERROR', { translate: true, errorResult: error });
                    },
                    complete: () => this.isProcessingService.StopProcessing()
                });
            sub.add(() => this.isProcessingService.StopProcessing());
            this.currentSearch = sub;
        }
    }

    private rawSearchResult: CompactServiceCase[] = [];

    private processResults(searchString: string, result: CollectionResult<CompactServiceCase>) {
        var items = result.items;
        if (result.count > 1) {
            if (this.myservicecases) {
                items = items.filter(r => ["closed", "cancelled"].indexOf(r.status) == -1 && r.createdBy == this.logedInUsername);
            } else {
                items = items.filter(r => ["closed", "cancelled"].indexOf(r.status) == -1)
            }
        }
        this.checkResult(result);
        this.rawSearchResult = items;
        this.filterAccounts(this.application.selectedCustomers);
        this.showtextnoresults = true;
        this.tomuch = result.count > 100;

    }

    private checkResult(result): void {
        this.searchResultPageCount = result.pageCount;
        this.tomuch = this.checkTooManyResults();
    }

    private checkTooManyResults(): boolean {
        return !!this.searchResultPageCount && this.searchResultPageCount > 1;
    }

    private filterAccounts(selected) {
        this.SearchResult = this.rawSearchResult.filter(x => selected.some(y => y.key == x.customer.href.replace('customer/', '')));
    }

    private selectedAccountsChanged(selected: { key: string, text: string }[]) {
        this.filterAccounts(selected);
        this.getCounters();
        switch (this.lastSearchMode) {
            case 1:
                if (this._searchPhrase)
                    this.Search(this._searchPhrase);
                break;
            case 2:
                this.onActionRequiredClicked();
                break;
            case 3:
                this.onAwaitingFeedbackClicked();
                break;
            case 4:
                this.onActiveCasesClicked();
                break;
        }
    }

    public onAwaitingFeedbackClicked(): void {
        this.hint = this.needsfeedbacklabel;
        this._searchPhrase = '';
        this.lastSearchMode = 3;
        var custIds = this.application.selectedCustomers.map(x => x.key);
        if (custIds.length > 0) {
            this.isProcessingService.StartProcessing();
            var sub = Rx.Observable.fromPromise(
                this.serviceCaseService.AwaitingFeedback(custIds, 100, this._myservicecases))
                .subscribe({
                    next: result => {
                        var items = result.items;
                        this.SearchResult = items.map(y => y);
                        this.checkResult(result);
                    },
                    error: error => {
                        if (ServiceUtils.IsUnauthorizedResponse(error)) {
                            return;
                        }
                    },
                    complete: () => this.isProcessingService.StopProcessing()
                })
            sub.add(() => this.isProcessingService.StopProcessing());
            this.currentSearch = sub;
        } else {
            this.SearchResult = [];
        }
    }

    public onActionRequiredClicked() {
        this.hint = this.requiresactionlabel;
        this._searchPhrase = '';
        this.lastSearchMode = 2;
        var custIds = this.application.selectedCustomers.map(x => x.key);
        if (custIds.length > 0) {
            this.isProcessingService.StartProcessing();
            var sub = Rx.Observable.fromPromise(this.serviceCaseService.ActionRequired(custIds, 100, this._myservicecases))
                .subscribe({
                    next: result => {
                        this.SearchResult = result.items.map(y => y)
                        this.checkResult(result);
                    },
                    error: error => {
                        if (ServiceUtils.IsUnauthorizedResponse(error)) {
                            return;
                        }
                    },
                    complete: () => this.isProcessingService.StopProcessing()
                })
            sub.add(() => this.isProcessingService.StopProcessing());
            this.currentSearch = sub;
        } else {
            this.SearchResult = [];
        }
    }

    public onActiveCasesClicked() {
        this.hint = this.activecaseslabel;
        this._searchPhrase = '';
        this.lastSearchMode = 4;
        var custIds = this.application.selectedCustomers.map(x => x.key);
        if (custIds.length > 0) {
            this.isProcessingService.StartProcessing();
            var sub = Rx.Observable.fromPromise(this.serviceCaseService.ActiveCases(custIds, 100, this._myservicecases))
                .subscribe({
                    next: result => {
                        this.SearchResult = result.items.map(y => y)
                        this.checkResult(result);
                    },
                    error: error => {
                        if (ServiceUtils.IsUnauthorizedResponse(error)) {
                            return;
                        }
                    },
                    complete: () => this.isProcessingService.StopProcessing()
                })
            sub.add(() => this.isProcessingService.StopProcessing());
            this.currentSearch = sub;
        } else {
            this.SearchResult = [];
        }
    }


    public async createXlsx() {
        if (this.application.selectedCustomers && this.application.selectedCustomers.length > 0) {
            var custs = this.application.selectedCustomers.map(v => v.key);
            try {
                var result;
                this.isProcessingService.StartProcessing();
                switch (this.lastSearchMode) {
                    case 0:
                    case 1:
                    case 5:
                        result = await this.serviceCaseService.FilteredExport(this._searchPhrase, custs)
                        break;
                    case 2:
                        result = await this.serviceCaseService.ActionRequiredExport(custs, this._myservicecases);
                        break;
                    case 3:
                        result = await this.serviceCaseService.AwaitingFeedbackExport(custs, this._myservicecases);
                        break;
                    case 4:
                        result = await this.serviceCaseService.ActiveCasesExport(custs, this._myservicecases);
                        break;
                    default:
                        throw "Invalidmode";
                }
                this.processResults(this._searchPhrase, result);
                this.exportService.saveExcelFromJson(this.SearchResult, null);
            } catch (error) {
                if (ServiceUtils.IsUnauthorizedResponse(error)) {
                    return;
                }
                this.application.setSeqUrl(error);
                this.logService.error('Search', 'Error while searching', error);
                await this.dialogService.showErrorDialog('error', 'ERROR', 'SEARCH_ERROR', { errorResult: error, translate: true });
            } finally {
                this.showtextnoresults = true;
                this.isProcessingService.StopProcessing();
            }
        }
    }
}
